import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { CERTIFICATIONS, EDUCATION } from '../../constants';
import SectionHeading from '../UI/SectionHeading';

interface CertProps {
  isDetailed?: boolean;
}

const Certifications: React.FC<CertProps> = ({ isDetailed = false }) => {
  return (
    <section id='certifications' className='scroll-mt-20 max-w-7xl mx-auto px-5 sm:px-6'>
      {!isDetailed && (
        <SectionHeading
          index='06'
          kicker='Credentials'
          title='Education & certifications'
          description='The formal foundation under the practical work.'
        />
      )}

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-x-14 gap-y-14'>
        {/* Education */}
        <div>
          <div className='label-mono text-gray2 mb-5'>Education</div>
          <div className='border-t border-line'>
            {EDUCATION.map((edu, i) => (
              <motion.div
                key={i}
                initial={isDetailed ? false : { opacity: 0, y: 20 }}
                animate={isDetailed ? { opacity: 1, y: 0 } : undefined}
                whileInView={isDetailed ? undefined : { opacity: 1, y: 0 }}
                viewport={isDetailed ? undefined : { once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                className='group py-6 border-b border-line'
              >
                <div className='flex items-baseline justify-between gap-4'>
                  <h3 className='font-medium tracking-tight text-lg text-ink group-hover:translate-x-1.5 transition-transform duration-400'>
                    {edu.degree}
                  </h3>
                  <span className='label-mono text-gray2 shrink-0'>{edu.year}</span>
                </div>
                <div className='mt-1.5 text-sm text-gray1'>{edu.major}</div>
                <div className='label-mono !text-[10px] text-gray2 mt-2'>
                  {edu.institution}, {edu.location}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div>
          <div className='label-mono text-gray2 mb-5'>Certifications</div>
          <div className='border-t border-line'>
            {CERTIFICATIONS.map((cert, index) => (
              <motion.a
                key={index}
                href={cert.url}
                target='_blank'
                rel='noopener noreferrer'
                data-cursor='pointer'
                initial={isDetailed ? false : { opacity: 0, y: 20 }}
                animate={isDetailed ? { opacity: 1, y: 0 } : undefined}
                whileInView={isDetailed ? undefined : { opacity: 1, y: 0 }}
                viewport={isDetailed ? undefined : { once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
                className='group flex items-center gap-4 py-4 sm:py-5 border-b border-line'
              >
                <span className='label-mono text-gray2 w-7 shrink-0'>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className='flex-1 min-w-0'>
                  <span className='block font-medium tracking-tight text-base text-ink truncate group-hover:translate-x-1.5 transition-transform duration-400'>
                    {cert.name}
                  </span>
                  <span className='label-mono !text-[10px] text-gray2'>
                    {cert.issuer} · {cert.year}
                  </span>
                </span>
                <span className='w-10 h-10 shrink-0 rounded-full border border-line flex items-center justify-center text-gray1 transition-all duration-400 group-hover:bg-accent group-hover:border-accent group-hover:text-paper group-hover:rotate-45'>
                  <ArrowUpRight size={14} />
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Certifications;
